import { Appointments } from "../../services/appointments";
import { isToday, isPast, parseISO } from "date-fns";

const ACCOUNT_TYPES = {
  doctor: "doctors",
  receptionist: "doctors",
  patient: "users"
};

// Busca uma das abas de consultas. Com `page`, a lista vem paginada e o total
// geral chega no header Total-Count (o corpo continua sendo a lista pura).
function fetchFiltered({ commit, rootGetters }, filter, mutation, page) {
  const currentUser = rootGetters["user/getCurrentUser"];
  const source = ACCOUNT_TYPES[currentUser.account_type];

  return Appointments.fetchFiltered(source, filter, page)
    .then(response => {
      commit(mutation, response.data);
      if (page) {
        const total = Number(response.headers?.["total-count"]) || 0;
        commit("SET_APPOINTMENTS_TOTAL", { filter, total });
      }
      return response;
    })
    .catch(err => {
      return err;
    });
}

export const state = {
  currentSchedule: {},
  appointmentsList: [],
  futureAppointmentsList: [],
  pastAppointmentsList: [],
  canceledAppointmentsList: [],
  refundedAppointmentsList: [],
  // Total de cada filtro (header Total-Count) quando a lista é paginada.
  appointmentsTotals: { future: 0, past: 0, canceled: 0, refunded: 0 }
};

export const mutations = {
  SET_SCHEDULE(state, schedule) {
    state.currentSchedule = schedule;
  },
  UNSET_SCHEDULE(state) {
    state.currentSchedule = {};
  },
  SET_APPOINTMENTS(state, payload) {
    state.appointmentsList = payload;
  },
  SET_FUTURE_APPOINTMENTS(state, payload) {
    state.futureAppointmentsList = payload;
  },
  SET_PAST_APPOINTMENTS(state, payload) {
    state.pastAppointmentsList = payload;
  },
  SET_CANCELED_APPOINTMENTS(state, payload) {
    state.canceledAppointmentsList = payload;
  },
  SET_REFUNDED_APPOINTMENTS(state, payload) {
    state.refundedAppointmentsList = payload;
  },
  SET_APPOINTMENTS_TOTAL(state, { filter, total }) {
    state.appointmentsTotals = { ...state.appointmentsTotals, [filter]: total };
  }
};

export const getters = {
  todayAppointments(state) {
    return state.appointmentsList.filter(appointment => {
      if (
        isToday(parseISO(appointment.scheduled_at)) &&
        !isPast(parseISO(appointment.scheduled_at))
      )
        return appointment;
    });
  },
  scheduledAppointments(state) {
    return state.appointmentsList.filter(
      appointment => appointment.order && appointment.order.status === "pending"
    );
  },
  performedAppointments(state) {
    return state.appointmentsList.filter(appointment => {
      if (isPast(parseISO(appointment.scheduled_at))) return appointment;
    });
  },
  futureAppointments(state) {
    return state.futureAppointmentsList;
  },
  pastAppointments(state) {
    return [...state.pastAppointmentsList].sort(
      (a, b) => new Date(b.scheduled_at) - new Date(a.scheduled_at)
    );
  },
  canceledAppointments(state) {
    return state.canceledAppointmentsList;
  },
  refundedAppointments(state) {
    return state.refundedAppointmentsList;
  },
  appointmentsTotals(state) {
    return state.appointmentsTotals;
  },
  currentSchedule(state) {
    return state.currentSchedule;
  }
};

export const actions = {
  scheduleAppointment({}, payload) {
    return Appointments.create(payload);
  },

  cancelAppointment({}, payload) {
    return Appointments.cancelation({ appointment_cancelation: payload });
  },

  rescheduleAppointment({}, { appointmentId, payload }) {
    return Appointments.reschedule(appointmentId, payload);
  },

  cancelAdminAppointment({}, payload) {
    return Appointments.adminCancelation(payload.appointment_id, payload.note);
  },

  finishAppointment({}, appointmentId) {
    return Appointments.finish(appointmentId);
  },

  fetchAppointments({ commit, rootGetters }) {
    const currentUser = rootGetters["user/getCurrentUser"];

    const source = ACCOUNT_TYPES[currentUser.account_type];

    return Appointments.fetch(source)
      .then(response => {
        commit("SET_APPOINTMENTS", response.data);
        return response;
      })
      .catch(err => {
        return err;
      });
  },

  fetchFutureAppointments(context, { page } = {}) {
    return fetchFiltered(context, "future", "SET_FUTURE_APPOINTMENTS", page);
  },

  fetchPastAppointments(context, { page } = {}) {
    return fetchFiltered(context, "past", "SET_PAST_APPOINTMENTS", page);
  },

  fetchCanceledAppointments(context, { page } = {}) {
    return fetchFiltered(
      context,
      "canceled",
      "SET_CANCELED_APPOINTMENTS",
      page
    );
  },

  fetchRefundedAppointments(context, { page } = {}) {
    return fetchFiltered(
      context,
      "refunded",
      "SET_REFUNDED_APPOINTMENTS",
      page
    );
  },

  holdScheduleAppointment({ commit }, payload) {
    commit("SET_SCHEDULE", payload);
  },

  clearScheduleAppointment({ commit }) {
    commit("UNSET_SCHEDULE");
  },

  appointmentNotes({ commit }, { description, appointmentId }) {
    return Appointments.notes(description, appointmentId)
      .then(response => {
        return response;
      })
      .catch(err => {
        return err;
      });
  },

  fetchAppointmentNotes({ commit }, appointmentId) {
    return Appointments.fetchNotes(appointmentId)
      .then(response => {
        return response;
      })
      .catch(err => {
        return err;
      });
  },

  fetchAppointmentNotesByPatient({ commit }, patientId) {
    return Appointments.fetchNotesByPatient(patientId)
      .then(response => {
        return response;
      })
      .catch(err => {
        return err;
      });
  },

  createAppointmentByNurse({}, payload) {
    return Appointments.createAppointmentByNurse(payload);
  },

  showAppointment({}, payload) {
    return Appointments.showAppointment(payload);
  },

  fetchTestimonial({}, appointmentId) {
    return Appointments.fetchTestimonial(appointmentId);
  },
  createTestimonial({}, { appointmentId, payload }) {
    return Appointments.createTestimonial(appointmentId, payload);
  },
  fetchPendingPayment({}, appointmentId) {
    return Appointments.pendingPayment(appointmentId);
  }
};
